import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { PART_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { ContentItemViewNodesType } from '../types'

/** Utility functions for handling content item view nodes and product types
 * @param familyAddedProductTypes - List of product types added to the family
 * @param contentItem - The content item view node
 * @param elementType - The type of the project element
 * @returns true if the content item is of type Products and is part of a family, false otherwise
 * **/
export const isProductFamilyWithProductsPartType = (
    familyAddedProductTypes: ProjectElementNode[] | undefined,
    contentItem: ContentItemViewNodesType | undefined,
    elementType: ProjectElementType | undefined
): boolean =>
    (familyAddedProductTypes?.length ?? 0) > 0 &&
    !!contentItem &&
    elementType === ProjectElementType.PRODUCTFAMILY &&
    contentItem?.partType?.toUpperCase() === PART_TYPES.PRODUCTS

/**
 *
 * @param contentItem - The content item view node
 * @returns Array of content item IDs derived from the content item view node
 * This function extracts various IDs from the content item view node, including documentTemplateViewId, dtvItemId, id, dtvParentId, dtvContentItemParentId, and dtvContentItemId.
 **/
export const getContentItemIds = (
    contentItem: ContentItemViewNodesType | undefined
): string[] =>
    [
        contentItem?.documentTemplateViewId,
        contentItem?.dtvItemId,
        contentItem?.id,
        contentItem?.dtvParentId,
        contentItem?.dtvContentItemParentId,
        contentItem?.dtvContentItemId,
    ].filter(Boolean) as string[]

/**
 * Finds a matching family product type based on the selected project element node ID and content item IDs
 * @param familyAddedProductTypes - List of product types added to the family
 * @param selectedProjectElementNodeId - The ID of the selected project element node
 * @param contentItemIds - Array of content item IDs which should match the product type's baseElementId
 * @returns The matching family product type or undefined if not found
 **/
export const findMatchingFamilyProductType = (
    familyAddedProductTypes: ProjectElementNode[] | undefined,
    selectedProjectElementNodeId: string | undefined,
    contentItemIds: string[]
): ProjectElementNode | undefined => {
    const contentItemIdSet = new Set(contentItemIds)
    return familyAddedProductTypes?.find(
        (productType) =>
            selectedProjectElementNodeId === productType.parentNodeId &&
            contentItemIdSet.has(productType.baseElementId)
    )
}
